import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { fmtMoney, fmtDiscount, lineAmount, formatAxiosError } from "../lib/api";
import { fmtSiret, isValidSiret } from "../lib/siret";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, ArrowLeft, ArrowRight, Copy, Share2, Loader2, Search } from "lucide-react";

const DOCUMENT_TYPES = { kbis: "Extrait K-bis", piece_identite: "Pièce d'identité", rib: "RIB", autre: "Autre document" };
const CLIENT_TYPE_OPTIONS = [
  { value: "particulier", label: "Particulier" },
  { value: "tpe", label: "Professionnel — 5 salariés ou moins" },
  { value: "pro", label: "Professionnel — plus de 5 salariés" },
];
const STEP_LABELS = ["Modèle", "Client", "Services", "Récapitulatif"];
const monthsLabel = (m) => (m === 0 ? "Sans engagement" : `${m} mois`);
const PRO_TYPES = ["tpe", "pro"];

/**
 * Assistant partenaire en 4 étapes (mobile d'abord). Un seul composant, état local unique :
 * les 4 étapes se rendent conditionnellement dans le même arbre (pas de démontage entre
 * étapes) — l'état saisi survit à la navigation précédent/suivant sans contexte ni lib dédiée.
 * Aucun prix n'est calculé ici : POST /partner/quote-preview fait autorité (anti-rebond 400 ms).
 */
export default function PartnerNewProposal() {
  const [step, setStep] = useState(0);

  // Étape 1 — Modèle. `undefined` = pas encore choisi, `null` = "Sans modèle" (catalogue complet).
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState(undefined);

  // Étape 2 — Client
  const [clients, setClients] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientMode, setClientMode] = useState("existing");
  const [existingClientId, setExistingClientId] = useState("");
  const [newClient, setNewClient] = useState({ name: "", email: "", phone: "" });
  const [clientType, setClientType] = useState("");
  const [language, setLanguage] = useState("fr"); // langue de la proposition, des emails et du contrat (fiche client)
  // Client pro (tpe | pro) : société choisie dans l'annuaire des entreprises — { company, siret, address }.
  const [proCompany, setProCompany] = useState(null);

  // Étape 3 — Services & options
  const [catalog, setCatalog] = useState(null);
  const [sel, setSel] = useState({}); // { [productId]: { enabled, quantity, commitmentMonths, options: { [childId]: {enabled, quantity} } } }
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef(null);

  // Étape 4 — Récapitulatif
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  // Offre fibre : numéro saisi, ou « à fournir par le client » (il le saisira avant de signer).
  const [fibreNumber, setFibreNumber] = useState("");
  const [fibreByClient, setFibreByClient] = useState(false);
  // Ligne téléphonique : portabilité (numéro + RIO facultatif) ou nouveau numéro.
  const [phoneMode, setPhoneMode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneRio, setPhoneRio] = useState("");
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get("/partner/templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
    api.get("/partner/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    api.get("/partner/catalog").then((r) => setCatalog(r.data)).catch(() => setCatalog([]));
  }, []);

  // Changement de modèle (y compris "Sans modèle") : la sélection précédente ne s'applique
  // plus (services proposables différents) — on repart d'une sélection vierge.
  useEffect(() => { setSel({}); }, [templateId]);

  // Autre client (existant différent, ou nouveau client à la place de l'existant) : le numéro à conserver et le RIO
  // saisis pour le précédent ne sont pas les siens — ni au récapitulatif, ni dans la requête. (Le n° de fibre reste tel quel.)
  useEffect(() => { setPhoneNumber(""); setPhoneRio(""); }, [clientMode, existingClientId]);

  const selectedTemplate = (templates || []).find((t) => t.id === templateId) || null;
  const presetProducts = selectedTemplate?.catalog_preset?.products || [];
  const presetIds = presetProducts.length ? new Set(presetProducts.map((p) => p.product_id)) : null;
  const presetById = new Map(presetProducts.map((p) => [p.product_id, p]));
  // Modèle avec préréglages non vides ⇒ uniquement ses services ; sinon tout le catalogue.
  const catalogItems = (catalog || []).filter((p) => !presetIds || presetIds.has(p.id));

  // Lien « capacité » (ex. TV) : le service se commande par nombre total ; miroir de capacityLink (catalog.helpers.ts).
  const capacityLinkOf = (p) => (p?.links || []).find((l) => !l.required && l.quantity_mode === "fixed"
    && l.included_per_parent != null && l.max_per_parent != null);
  // Affichage seulement (le serveur fait autorité) : miroir de capacitySplit.
  const capacitySplit = (total, l, p) => {
    const qty = Math.max(p.min_qty ?? 1, Math.ceil(total / (l.included_per_parent + l.max_per_parent)));
    return { qty, extra: Math.max(0, total - l.included_per_parent * qty) };
  };

  const defaultState = (p) => ({
    enabled: !!presetById.get(p?.id)?.preselected,
    quantity: presetById.get(p?.id)?.quantity ?? 1,
    // Service saisi par total (ex. TV) : la quantité préréglée du modèle est le nombre total.
    total: presetById.get(p?.id)?.quantity ?? 1,
    commitmentMonths: p?.commitment_options?.[0]?.months ?? 0,
    options: {},
  });
  const getState = (p) => sel[p.id] || defaultState(p);
  const getOption = (state, childId, fallbackQty) => state.options[childId] || { enabled: false, quantity: fallbackQty || 1 };

  const patchSel = (id, patch) => setSel((s) => {
    const base = s[id] || defaultState(catalogItems.find((p) => p.id === id));
    return { ...s, [id]: { ...base, ...patch } };
  });
  const patchOption = (parentId, childId, patch, fallbackQty) => setSel((s) => {
    const parent = s[parentId] || defaultState(catalogItems.find((p) => p.id === parentId));
    const current = getOption(parent, childId, fallbackQty);
    return { ...s, [parentId]: { ...parent, options: { ...parent.options, [childId]: { ...current, ...patch } } } };
  });

  // Miroir de SelectionLine (catalog.helpers.ts) : seuls les services cochés, avec leurs
  // options non obligatoires cochées. Les liens obligatoires ne sont jamais envoyés — le
  // serveur les ajoute et les verrouille lui-même.
  const buildSelection = () => catalogItems
    .filter((p) => getState(p).enabled)
    .map((p) => {
      const s = getState(p);
      const cap = capacityLinkOf(p);
      const options = (p.links || [])
        .filter((l) => l !== cap && !l.required && getOption(s, l.product.id, l.quantity).enabled)
        .map((l) => ({
          product_id: l.product.id,
          ...(l.quantity_mode === "fixed" ? { quantity: Number(getOption(s, l.product.id, l.quantity).quantity) || l.quantity || 1 } : {}),
        }));
      return {
        product_id: p.id,
        ...(cap ? { total: Math.max(1, Math.round(Number(s.total)) || 1) } : { quantity: Number(s.quantity) || 1 }),
        commitment_months: s.commitmentMonths ?? 0,
        ...(options.length ? { options } : {}),
      };
    });

  // Aperçu serveur anti-rebond (~400 ms) : seul endroit qui calcule un prix. Toute erreur 400
  // (ex. engagement > 24 mois pour un particulier) s'affiche telle quelle, sans reproduire la
  // règle légale côté client.
  useEffect(() => {
    if (step !== 2) return undefined;
    const selection = buildSelection();
    if (selection.length === 0) { setPreview(null); setPreviewError("Sélectionnez au moins un service."); return undefined; }
    setPreviewError("");
    setPreviewLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post("/partner/quote-preview", { ...(templateId ? { template_id: templateId } : {}), client_type: clientType, selection })
        .then((r) => setPreview(r.data))
        .catch((e) => { setPreview(null); setPreviewError(formatAxiosError(e)); })
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, step]);

  const filteredClients = (clients || []).filter((c) => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  const selectedClient = (clients || []).find((c) => c.id === existingClientId);
  // SIRET exigé pour un pro (nouveau client, ou client existant qui ne l'a pas encore) — même règle côté serveur.
  const needsSiret = PRO_TYPES.includes(clientType) && (clientMode === "new" || (!!selectedClient && !selectedClient.siret));
  const siretOk = !needsSiret || (!!proCompany?.company?.trim() && isValidSiret(proCompany.siret));
  const canNext = [
    templateId !== undefined,
    !!clientType && siretOk && (clientMode === "existing" ? !!existingClientId : !!newClient.name.trim() && !!newClient.email.trim()),
    !!preview && !previewError,
  ];
  const needsFibre = !!preview?.fibre_number_required; // même règle côté serveur
  const fibreOk = !needsFibre || fibreByClient || !!fibreNumber.trim();
  const needsPhone = !!preview?.phone_number_required; // même règle côté serveur
  const phoneOk = !needsPhone || phoneMode === "nouveau" || (phoneMode === "portabilite" && !!phoneNumber.trim());

  const resetWizard = () => {
    setStep(0); setTemplateId(undefined); setClientMode("existing"); setExistingClientId("");
    setNewClient({ name: "", email: "", phone: "" }); setClientType(""); setLanguage("fr"); setProCompany(null);
    setSel({}); setPreview(null); setPreviewError(""); setTitle(""); setCreated(null);
    setFibreNumber(""); setFibreByClient(false);
    setPhoneMode(""); setPhoneNumber(""); setPhoneRio("");
  };

  const save = async (andSend) => {
    setSaving(true);
    try {
      const body = {
        ...(templateId ? { template_id: templateId } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        client: {
          ...(clientMode === "existing"
            ? { id: existingClientId }
            : { name: newClient.name.trim(), email: newClient.email.trim(), phone: newClient.phone.trim() || undefined }),
          client_type: clientType,
          language,
          ...(needsSiret && { company: proCompany.company.trim(), siret: proCompany.siret }),
        },
        client_type: clientType,
        selection: buildSelection(),
        ...(needsFibre && (fibreByClient ? { fibre_number_by_client: true } : { fibre_number: fibreNumber.trim() })),
        ...(needsPhone && { phone_number_mode: phoneMode }),
        ...(needsPhone && phoneMode === "portabilite" && { phone_number: phoneNumber.trim(), ...(phoneRio.trim() && { phone_rio: phoneRio.trim() }) }),
      };
      const { data } = await api.post("/partner/proposals", body);
      const result = andSend ? (await api.post(`/partner/proposals/${data.id}/send`)).data : data;
      setCreated(result);
    } catch (e) {
      toast.error(formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(created.public_url); toast.success("Lien copié"); };
  const shareLink = () => {
    if (navigator.share) { navigator.share({ title: created.title, url: created.public_url }).catch(() => {}); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(created.public_url)}`, "_blank", "noopener,noreferrer");
  };

  if (created) {
    return (
      <div className="p-6 md:p-8 max-w-lg mx-auto text-center" data-testid="wizard-success">
        <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7" />
        </div>
        <h1 className="font-heading text-2xl font-bold mb-1">
          Proposition {created.emailed_to !== undefined ? "envoyée" : "enregistrée"}
        </h1>
        <p className="text-sm text-muted-foreground mb-4">{created.client_name}</p>
        {created.emailed_to && <p className="text-sm text-emerald-700 mb-1">Email envoyé à {created.emailed_to}</p>}
        {created.email_error && <p className="text-sm text-amber-700 mb-1" data-testid="wizard-email-error">{created.email_error}</p>}
        <div className="flex items-center gap-2 mt-4 border border-border rounded-sm p-2.5 bg-card">
          <input readOnly value={created.public_url} data-testid="wizard-public-link" className="flex-1 text-xs bg-transparent outline-none px-1" />
          <button onClick={copyLink} data-testid="wizard-copy-link" title="Copier" className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={shareLink} data-testid="wizard-share-link" title="Partager" className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-3 mt-6 justify-center">
          <Button variant="outline" onClick={() => window.open(created.public_url, "_blank", "noopener,noreferrer")} data-testid="wizard-view-proposal" className="rounded-sm">
            Voir la proposition
          </Button>
          <Button onClick={resetWizard} data-testid="wizard-new-proposal" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            Nouvelle proposition
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-3xl" data-testid="partner-wizard">
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Portail partenaire</p>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-6">Nouvelle proposition</h1>

      <div className="flex items-center gap-1.5 mb-8" data-testid="wizard-steps">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div
                data-testid={`wizard-step-dot-${i}`}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${
                  i < step ? "bg-primary text-white" : i === step ? "bg-primary text-white ring-4 ring-primary/20" : "bg-muted text-muted-foreground"
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-[10px] text-center leading-tight ${i === step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && <div className={`h-px flex-1 mx-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="wizard-step-template">
          {templates === null ? (
            <p className="text-sm text-muted-foreground col-span-2">Chargement…</p>
          ) : (
            <>
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => setTemplateId(t.id)} data-testid={`template-card-${t.id}`}
                  className={`text-left border rounded-sm p-4 transition-all ${templateId === t.id ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <p className="text-sm font-semibold">{t.name}</p>
                  {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                </button>
              ))}
              <button type="button" onClick={() => setTemplateId(null)} data-testid="template-none"
                className={`text-left border rounded-sm p-4 transition-all ${templateId === null ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "border-border hover:border-primary/50"}`}>
                <p className="text-sm font-semibold">Sans modèle</p>
                <p className="text-xs text-muted-foreground mt-1">Catalogue complet</p>
              </button>
            </>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="wizard-step-client">
          <div className="flex gap-2">
            <Button type="button" variant={clientMode === "existing" ? "default" : "outline"} onClick={() => { setClientMode("existing"); setProCompany(null); }} data-testid="client-mode-existing" className="rounded-sm">
              Client existant
            </Button>
            <Button type="button" variant={clientMode === "new" ? "default" : "outline"} onClick={() => { setClientMode("new"); setProCompany(null); }} data-testid="client-mode-new" className="rounded-sm">
              Nouveau client
            </Button>
          </div>

          {clientMode === "existing" ? (
            <div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Rechercher un client…"
                  data-testid="client-search" className="pl-9 rounded-sm" />
              </div>
              <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5">
                {clients === null && <p className="text-sm text-muted-foreground py-4">Chargement…</p>}
                {clients !== null && filteredClients.length === 0 && <p className="text-sm text-muted-foreground py-4">Aucun client.</p>}
                {filteredClients.map((c) => (
                  <button key={c.id} type="button" data-testid={`client-existing-${c.id}`}
                    onClick={() => { setExistingClientId(c.id); setClientType(c.client_type || ""); setLanguage(c.language || "fr"); setProCompany(null); }}
                    className={`w-full text-left border rounded-sm px-3 py-2 text-sm transition-colors ${existingClientId === c.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                    <p className="font-medium">{c.name}{c.company ? ` — ${c.company}` : ""}{c.language === "en" && <span className="ml-2 text-[10px] font-semibold border border-border rounded-sm px-1 py-0.5 text-muted-foreground">EN</span>}</p>
                    <p className="text-xs text-muted-foreground">{c.email}{c.siret && ` · SIRET ${fmtSiret(c.siret)}`}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-w-sm">
              <div>
                <Label htmlFor="new-client-name">Nom du contact</Label>
                <Input value={newClient.name} onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))} id="new-client-name" data-testid="new-client-name" className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="new-client-email">Email *</Label>
                <Input type="email" value={newClient.email} onChange={(e) => setNewClient((c) => ({ ...c, email: e.target.value }))} id="new-client-email" data-testid="new-client-email" className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="new-client-phone">Téléphone (optionnel)</Label>
                <Input value={newClient.phone} onChange={(e) => setNewClient((c) => ({ ...c, phone: e.target.value }))} id="new-client-phone" data-testid="new-client-phone" className="mt-1.5 rounded-sm" />
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium mb-1">Type de client</p>
            <p className="text-xs text-muted-foreground mb-2">Détermine le droit de rétractation et les pièces demandées.</p>
            <RadioGroup value={clientType} onValueChange={setClientType} className="space-y-2">
              {CLIENT_TYPE_OPTIONS.map((o) => (
                <label key={o.value} data-testid={`client-type-${o.value}`}
                  className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 cursor-pointer transition-colors ${clientType === o.value ? "border-primary bg-primary/5" : "border-border"}`}>
                  <RadioGroupItem value={o.value} />
                  <span className="text-sm">{o.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Langue du client</p>
            <p className="text-xs text-muted-foreground mb-2">Proposition, emails et contrat dans cette langue.</p>
            <div className="flex gap-2">
              {[["fr", "Français"], ["en", "English"]].map(([v, label]) => (
                <Button key={v} type="button" variant={language === v ? "default" : "outline"} onClick={() => setLanguage(v)}
                  aria-pressed={language === v} data-testid={`client-language-${v}`} className="rounded-sm">{label}</Button>
              ))}
            </div>
          </div>

          {needsSiret && (
            <div data-testid="client-company">
              <p className="text-sm font-medium mb-1">Société *</p>
              <p className="text-xs text-muted-foreground mb-2">Recherchez-la dans l'annuaire des entreprises : raison sociale et SIRET sont repris automatiquement.</p>
              <CompanyPicker key={clientMode === "existing" ? existingClientId : "new"} value={proCompany} onChange={setProCompany}
                initialQuery={clientMode === "existing" ? selectedClient?.company || "" : ""} />
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="wizard-step-services">
          {catalog === null ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : catalogItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun service proposable.</p>
          ) : (
            Object.entries(
              catalogItems.reduce((acc, p) => {
                const cat = p.category || "Autres";
                (acc[cat] = acc[cat] || []).push(p);
                return acc;
              }, {})
            ).map(([category, items]) => (
              <div key={category} className="space-y-2">
                <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{category}</h3>
                <div className="space-y-2">
                  {items.map((p) => {
                    const s = getState(p);
                    const commitments = p.commitment_options || [];
                    const requiredLinks = (p.links || []).filter((l) => l.required);
                    const cap = capacityLinkOf(p);
                    const optionalLinks = (p.links || []).filter((l) => !l.required && l !== cap);
                    const total = Math.max(1, Math.round(Number(s.total)) || 1);
                    const split = cap ? capacitySplit(total, cap, p) : null;
                    return (
                      <div key={p.id} className="border border-border rounded-sm p-4 bg-card" data-testid={`wizard-service-${p.id}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox checked={s.enabled} onCheckedChange={(v) => patchSel(p.id, { enabled: !!v })} data-testid={`service-check-${p.id}`} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                              {p.name}
                              {p.is_telecom && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">Télécom</span>}
                            </p>
                            {p.description && <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>}
                            <p className="text-xs text-muted-foreground mt-1">{fmtMoney(commitments.find((c) => c.months === s.commitmentMonths)?.unit_price ?? p.unit_price)} HT {p.recurrence === "mensuel" ? "/mois" : "unique"}</p>
                          </div>
                        </label>

                        {s.enabled && (
                          <div className="mt-3 pl-7 space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              {cap ? (
                                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                  Nombre de {cap.unit_label}
                                  <Input type="number" min={1} value={s.total}
                                    onChange={(e) => patchSel(p.id, { total: e.target.value })}
                                    data-testid={`service-total-${p.id}`} className="w-16 h-8 rounded-sm text-xs" />
                                </label>
                              ) : (
                                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                  Quantité
                                  <Input type="number" min={p.min_qty ?? 1} max={p.max_qty ?? undefined} value={s.quantity}
                                    onChange={(e) => patchSel(p.id, { quantity: e.target.value })}
                                    data-testid={`service-qty-${p.id}`} className="w-16 h-8 rounded-sm text-xs" />
                                </label>
                              )}
                              {commitments.length > 1 ? (
                                <Select value={String(s.commitmentMonths)} onValueChange={(v) => patchSel(p.id, { commitmentMonths: Number(v) })}>
                                  <SelectTrigger data-testid={`service-commitment-${p.id}`} className="w-44 h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {commitments.map((c) => (
                                      <SelectItem key={c.months} value={String(c.months)}>{monthsLabel(c.months)} — {fmtMoney(c.unit_price ?? p.unit_price)}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-muted-foreground" data-testid={`service-commitment-static-${p.id}`}>{monthsLabel(commitments[0]?.months ?? 0)}</span>
                              )}
                            </div>

                            {split && (
                              <p className="text-xs text-muted-foreground" data-testid={`service-breakdown-${p.id}`}>
                                = {split.qty} × {p.name} ({total - split.extra} {cap.unit_label} avec l'offre)
                                {split.extra > 0 && <> + {split.extra} × {cap.product.name}</>}
                              </p>
                            )}

                            {requiredLinks.length > 0 && (
                              <div className="text-xs text-muted-foreground space-y-1">
                                {requiredLinks.map((l) => (
                                  <p key={l.product.id}>Inclus : {l.product.name}{l.quantity_mode === "fixed" && l.quantity > 1 ? ` ×${l.quantity}` : ""}</p>
                                ))}
                              </div>
                            )}

                            {optionalLinks.length > 0 && (
                              <div className="space-y-1.5 pt-1 border-t border-border">
                                {optionalLinks.map((l) => {
                                  const os = getOption(s, l.product.id, l.quantity);
                                  return (
                                    <div key={l.product.id}>
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                      <Checkbox checked={os.enabled} onCheckedChange={(v) => patchOption(p.id, l.product.id, { enabled: !!v }, l.quantity)}
                                        data-testid={`option-check-${p.id}-${l.product.id}`} />
                                      <span className="flex-1">{l.product.name}</span>
                                      {l.quantity_mode === "fixed" && os.enabled && (
                                        <Input type="number" min={l.product.min_qty ?? 1}
                                          max={l.max_per_parent != null ? Math.min(l.product.max_qty ?? Infinity, l.max_per_parent * (Number(s.quantity) || 1)) : l.product.max_qty ?? undefined}
                                          value={os.quantity}
                                          onChange={(e) => patchOption(p.id, l.product.id, { quantity: e.target.value }, l.quantity)}
                                          data-testid={`option-qty-${p.id}-${l.product.id}`} className="w-14 h-7 rounded-sm text-xs" />
                                      )}
                                    </label>
                                    {l.max_per_parent != null && os.enabled && (
                                      <p className="text-[11px] text-muted-foreground pl-6 mt-0.5" data-testid={`option-cap-${p.id}-${l.product.id}`}>
                                        {l.max_per_parent} max par « {p.name} » — pour plus, augmentez la quantité ci-dessus.
                                      </p>
                                    )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          <PreviewSummary preview={preview} loading={previewLoading} error={previewError} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5" data-testid="wizard-step-recap">
          <div>
            <Label htmlFor="wizard-title">Titre (optionnel)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${selectedTemplate?.name || "Proposition"} - ${(needsSiret && proCompany?.company) || (clientMode === "existing" ? selectedClient?.company || selectedClient?.name : newClient.name) || ""}`}
              id="wizard-title" data-testid="wizard-title" className="mt-1.5 rounded-sm"
            />
          </div>

          <div className="border border-border rounded-sm p-4 bg-card space-y-1">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-1">Client</p>
            {clientMode === "existing" ? (
              <p className="text-sm">{selectedClient?.name}</p>
            ) : (
              <p className="text-sm">{newClient.name} · {newClient.email}</p>
            )}
            {PRO_TYPES.includes(clientType) && (
              <p className="text-sm" data-testid="recap-company">
                {(needsSiret ? proCompany?.company : selectedClient?.company)} — SIRET {fmtSiret(needsSiret ? proCompany?.siret : selectedClient?.siret)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{CLIENT_TYPE_OPTIONS.find((o) => o.value === clientType)?.label} · {language === "en" ? "En anglais" : "En français"}</p>
          </div>

          {preview?.required_documents?.length > 0 && (
            <div className="border border-border rounded-sm p-4">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Pièces qui seront demandées</p>
              <div className="flex flex-wrap gap-2">
                {preview.required_documents.map((t) => (
                  <span key={t} className="text-xs px-2 py-1 rounded-sm border border-border bg-muted/40">{DOCUMENT_TYPES[t] || t}</span>
                ))}
              </div>
            </div>
          )}

          {needsFibre && (
            <div className="border border-border rounded-sm p-4 space-y-3" data-testid="fibre-number-block">
              <div>
                <Label htmlFor="fibre-number">Numéro de fibre *</Label>
                <Input id="fibre-number" value={fibreNumber} maxLength={100} disabled={fibreByClient}
                  onChange={(e) => setFibreNumber(e.target.value)} placeholder="Numéro de la ligne fibre du client"
                  data-testid="fibre-number" className="mt-1.5 rounded-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="fibre-by-client" checked={fibreByClient} onCheckedChange={(v) => setFibreByClient(v === true)} data-testid="fibre-number-by-client" />
                <Label htmlFor="fibre-by-client" className="font-normal">À fournir par le client : il le saisira avant de signer</Label>
              </div>
            </div>
          )}

          {needsPhone && (
            <div className="border border-border rounded-sm p-4 space-y-3" data-testid="phone-line-block">
              <p className="text-sm font-medium">Ligne téléphonique *</p>
              <RadioGroup value={phoneMode} onValueChange={setPhoneMode} className="space-y-2">
                {[["portabilite", "Garder le numéro actuel (portabilité)"], ["nouveau", "Nouveau numéro"]].map(([value, label]) => (
                  <label key={value} data-testid={`phone-mode-${value}`}
                    className={`flex items-center gap-2 border rounded-sm px-3 py-2.5 cursor-pointer transition-colors ${phoneMode === value ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value={value} />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </RadioGroup>
              {phoneMode === "portabilite" && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="phone-number">Numéro à conserver *</Label>
                    <Input id="phone-number" value={phoneNumber} inputMode="tel" maxLength={20} onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="0590 27 12 34" data-testid="phone-number" className="mt-1.5 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="phone-rio">RIO (facultatif pour l'instant)</Label>
                    <Input id="phone-rio" value={phoneRio} maxLength={20} autoComplete="off" spellCheck={false} onChange={(e) => setPhoneRio(e.target.value)}
                      placeholder="12 lettres ou chiffres — à compléter plus tard si besoin" data-testid="phone-rio" className="mt-1.5 rounded-sm" />
                    <p className="text-xs text-muted-foreground mt-1">12 lettres ou chiffres. Sans RIO, le dossier reste incomplet tant que vous ne l'avez pas indiqué sur la proposition.</p>
                  </div>
                </div>
              )}
              {phoneMode === "nouveau" && <p className="text-xs text-muted-foreground">Un numéro sera attribué au client après la signature.</p>}
            </div>
          )}

          <PreviewSummary preview={preview} loading={previewLoading} error={previewError} />

          {!fibreOk && <p className="text-xs text-destructive" data-testid="fibre-number-missing">Saisissez le numéro de fibre, ou cochez « À fournir par le client ».</p>}
          {!phoneOk && <p className="text-xs text-destructive" data-testid="phone-line-missing">Ligne téléphonique : choisissez la portabilité (avec le numéro à conserver) ou un nouveau numéro.</p>}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button type="button" variant="outline" disabled={saving || !fibreOk || !phoneOk} onClick={() => save(false)} data-testid="wizard-save-draft" className="rounded-sm">
              {saving ? "Enregistrement…" : "Enregistrer en brouillon"}
            </Button>
            <Button type="button" disabled={saving || !fibreOk || !phoneOk} onClick={() => save(true)} data-testid="wizard-save-send" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {saving ? "Envoi…" : "Créer et envoyer"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between mt-8 pt-6 border-t border-border">
        <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wizard-prev" className="rounded-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Précédent
        </Button>
        {step < 3 && (
          <Button type="button" onClick={() => setStep((s) => Math.min(3, s + 1))} disabled={!canNext[step]} data-testid="wizard-next" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            Suivant <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}

/** Lignes + totaux calculés SERVEUR (aucun calcul de prix côté client). */
/**
 * Société d'un client pro : recherche dans l'annuaire des entreprises de l'État (via GET /partner/company-search,
 * anti-rebond 350 ms, réponses périmées ignorées) ; la suggestion choisie donne raison sociale + SIRET.
 * Introuvable ou annuaire indisponible : saisie manuelle (SIRET vérifié ici, puis par le serveur).
 */
function CompanyPicker({ value, onChange, initialQuery = "" }) {
  const [q, setQ] = useState(initialQuery);
  const [hits, setHits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    const id = ++seq.current;
    if (value || manual || term.length < 3) { setHits(null); setError(""); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/partner/company-search", { params: { q: term } })
        .then((r) => { if (id === seq.current) { setHits(r.data); setError(""); } })
        .catch((e) => { if (id === seq.current) { setHits(null); setError(formatAxiosError(e)); } })
        .finally(() => { if (id === seq.current) setLoading(false); });
    }, 350);
    return () => clearTimeout(t);
  }, [q, value, manual]);

  if (manual) {
    const siret = value?.siret || "";
    return (
      <div className="space-y-3 max-w-sm" data-testid="company-manual">
        <div>
          <Label htmlFor="company-manual-name">Raison sociale *</Label>
          <Input id="company-manual-name" data-testid="company-manual-name" value={value?.company || ""} className="mt-1.5 rounded-sm"
            onChange={(e) => onChange({ company: e.target.value, siret })} />
        </div>
        <div>
          <Label htmlFor="company-manual-siret">SIRET * (14 chiffres)</Label>
          <Input id="company-manual-siret" data-testid="company-manual-siret" inputMode="numeric" value={siret} className="mt-1.5 rounded-sm"
            onChange={(e) => onChange({ company: value?.company || "", siret: e.target.value.replace(/\D/g, "").slice(0, 14) })} />
          {siret.length === 14 && !isValidSiret(siret) && (
            <p className="text-xs text-rose-600 mt-1" data-testid="company-manual-invalid">SIRET invalide : vérifiez les chiffres.</p>
          )}
        </div>
        <button type="button" onClick={() => { setManual(false); onChange(null); }} className="text-xs text-primary underline underline-offset-2">
          Rechercher dans l'annuaire
        </button>
      </div>
    );
  }
  if (value) {
    return (
      <div className="border border-primary bg-primary/5 rounded-sm px-3 py-2.5 flex items-start justify-between gap-3" data-testid="company-selected">
        <div className="min-w-0 text-sm">
          <p className="font-medium">{value.company}</p>
          <p className="text-xs text-muted-foreground">SIRET {fmtSiret(value.siret)}{value.address && ` · ${value.address}`}</p>
        </div>
        <button type="button" onClick={() => onChange(null)} data-testid="company-change" className="text-xs text-primary underline underline-offset-2 shrink-0">
          Changer
        </button>
      </div>
    );
  }
  return (
    <div data-testid="company-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom de la société, SIREN ou SIRET" autoComplete="off"
          aria-label="Rechercher la société" data-testid="company-search-input" className="pl-9 pr-9 rounded-sm" />
        {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      </div>
      {error && <p className="text-xs text-rose-600 mt-2" data-testid="company-search-error">{error}</p>}
      {hits?.length === 0 && <p className="text-sm text-muted-foreground mt-2" data-testid="company-search-empty">Aucune société active trouvée.</p>}
      {hits?.length > 0 && (
        <div className="mt-2 max-h-72 overflow-y-auto space-y-1.5">
          {hits.map((h) => (
            <button key={h.siret} type="button" data-testid={`company-hit-${h.siret}`}
              onClick={() => onChange({ company: h.name, siret: h.siret, address: h.address })}
              className="w-full text-left border border-border hover:border-primary/50 rounded-sm px-3 py-2 text-sm transition-colors">
              <p className="font-medium">{h.name}</p>
              <p className="text-xs text-muted-foreground">SIRET {fmtSiret(h.siret)}{h.address && ` · ${h.address}`}</p>
            </button>
          ))}
        </div>
      )}
      <button type="button" onClick={() => { setManual(true); onChange(null); }} data-testid="company-manual-toggle"
        className="mt-2 text-xs text-primary underline underline-offset-2">
        Société introuvable ? Saisir le SIRET à la main
      </button>
    </div>
  );
}

function PreviewSummary({ preview, loading, error }) {
  return (
    <div className="border border-border rounded-sm p-4 bg-surface" data-testid="wizard-preview">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Aperçu</p>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>
      {error ? (
        <p className="text-sm text-rose-700" data-testid="wizard-preview-error">{error}</p>
      ) : !preview ? (
        <p className="text-sm text-muted-foreground">—</p>
      ) : (
        <div className="space-y-2">
          <div className="space-y-1">
            {preview.items.map((it) => (
              <p key={it.id || `${it.catalog_product_id}-${it.name}`} className="text-xs flex justify-between gap-2">
                <span className="text-muted-foreground truncate">
                  {it.name} — {it.quantity} × {fmtMoney(it.unit_price)}
                  {it.discount_type && ` (${fmtDiscount(it.discount_type, it.discount_value)})`}
                  {" · "}{monthsLabel(it.commitment_months || 0)}
                </span>
                <span className="shrink-0 font-medium">{fmtMoney(lineAmount(it))}{it.recurrence === "mensuel" ? "/mois" : ""}</span>
              </p>
            ))}
          </div>
          <div className="pt-2 border-t border-border flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold">
            {preview.totals.recurring > 0 && <span data-testid="wizard-preview-total-recurring">{fmtMoney(preview.totals.recurring)} HT/mois</span>}
            {preview.totals.one_shot > 0 && <span data-testid="wizard-preview-total-oneshot">{fmtMoney(preview.totals.one_shot)} HT (unique)</span>}
          </div>
          {preview.partner_pricing && (
            <p className="text-xs text-primary font-medium pt-1" data-testid="wizard-preview-margin">
              Votre marge : {fmtMoney(preview.partner_pricing.recurring)}/mois + {fmtMoney(preview.partner_pricing.one_shot)} unique
            </p>
          )}
        </div>
      )}
    </div>
  );
}
